import { motion } from "framer-motion";
import { Mail, Phone, Linkedin, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { Footer } from "@/components/utilities/Footer";
import { useEffect } from "react";
import { useTheme } from "@/contexts/ThemeContext";

// Define interface for team member data
interface TeamMember {
  name: string;
  role: string;
  description: string;
  email: string;
  linkedin: string;
  image: string;
  phone: string;
}

// ContactUs component with optional className prop
const ContactUs = ({ className = "" }: { className?: string }) => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  
  // Make sure we scroll to top when this page loads
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  
  // Team members data array
  const teamMembers: TeamMember[] = [
    {
      name: "Aniket Singh",
      role: "Founder & CEO",
      description: "Software developer with 2 years of experience specializing in backend systems, cloud architecture, and scalable solutions. Passionate about improving developer productivity through better documentation.",
      email: "aniket@docgen.dev",
      linkedin: "https://www.linkedin.com/in/aniket-singh-9a4860190/",
      image: "https://i.ibb.co/67M5RpLc/aniket.jpg",
      phone: "+1 (555) 987-6543",
    },
    {
      name: "Varshith G",
      role: "Co-founder & CTO",
      description: "Data engineering specialist with 3 years of experience in data pipelines and analytics. Leads DocGen's technical innovation to create intuitive and efficient developer tools.",
      email: "varshith@docgen.dev",
      linkedin: "https://www.linkedin.com/in/varshithg17/",
      image: "https://i.ibb.co/0Vm3Q3wh/varshith.jpg",
      phone: "+1 (555) 123-4567",
    },
  ];

  // Animation variants for container
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1, 
      transition: { 
        staggerChildren: 0.1, 
        delayChildren: 0.2
      }
    },
  };

  // Animation variants for individual items
  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { 
      y: 0, 
      opacity: 1, 
      transition: { 
        type: "spring", 
        stiffness: 100
      }
    },
  };

  const handleNavigateHome = () => {
    navigate('/');
  };

  return (
    <div className={`min-h-screen ${theme === 'dark' ? 'bg-[#030405]' : 'bg-background'} ${theme === 'dark' ? 'text-white' : 'text-gray-800'} relative overflow-hidden flex flex-col`}>
      {/* Background elements to match landing page */}
      <div className="absolute inset-0 z-0 pointer-events-none">
        {/* Consistent grid pattern across the entire page for dark mode */}
        {theme === 'dark' && (
          <div className="absolute inset-0 bg-[linear-gradient(to_right,#4f4f4f25_1px,transparent_1px),linear-gradient(to_bottom,#4f4f4f25_1px,transparent_1px)] bg-[size:15px_15px] opacity-40"></div>
        )}
        
        {/* Light mode grid pattern */}
        {theme !== 'dark' && (
          <div className={`absolute inset-0 light-grid-pattern bg-[size:14px_24px]`} />
        )}
        
        <motion.div
          className={`absolute top-1/4 -right-40 w-96 h-96 rounded-full blur-3xl ${theme === 'dark'
            ? 'bg-gradient-to-r from-primary/20 to-purple-500/10'
            : 'bg-gradient-to-r from-primary-light/20 to-purple-500/10'}`}
          animate={{ x: [0, 60, 0], y: [0, -40, 0], scale: [1, 1.2, 1], opacity: [0.5, 0.7, 0.5] }}
          transition={{ duration: 15, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div
          className={`absolute -top-20 left-1/3 w-72 h-72 rounded-full blur-3xl ${theme === 'dark'
            ? 'bg-gradient-to-r from-blue-500/15 to-primary/10'
            : 'bg-gradient-to-r from-blue-500/15 to-primary-light/10'}`}
          animate={{ x: [-20, 40, -20], y: [10, 40, 10], scale: [1.1, 0.9, 1.1], opacity: [0.3, 0.6, 0.3] }}
          transition={{ duration: 20, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div
          className="absolute bottom-1/3 -left-20 w-80 h-80 rounded-full blur-3xl bg-gradient-to-r from-blue-500/10 to-violet-500/15"
          animate={{ x: [0, -30, 0], y: [0, 50, 0], scale: [1, 1.3, 1], opacity: [0.4, 0.6, 0.4] }}
          transition={{ duration: 18, repeat: Infinity, ease: "easeInOut" }}
        />
      </div>

      <div className="container mx-auto px-4 sm:px-6 relative z-10 pt-16 sm:pt-20 flex-grow">
        <motion.div
          className={`py-16 ${className}`}
          initial="hidden"
          animate="visible"
          variants={containerVariants}
        >
          {/* Section header */}
          <div className="text-center mb-12">
            <motion.h2
              variants={itemVariants}
              className={`text-4xl sm:text-5xl font-bold ${theme === 'dark'
                ? 'text-transparent bg-clip-text bg-gradient-to-r from-white to-gray-400'
                : 'text-transparent bg-clip-text bg-gradient-to-r from-gray-800 to-gray-600'} mb-4`}
            >
              Meet Our Co-Founders
            </motion.h2>
            <motion.p
              variants={itemVariants}
              className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'} mt-2 text-lg max-w-2xl mx-auto`}
            >
              The visionaries driving DocGen's mission to transform documentation for developers worldwide
            </motion.p>
          </div>

          {/* Team members grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl mx-auto px-6">
            {teamMembers.map((member, index) => (
              <motion.div
                key={index}
                className={`rounded-xl ${theme === 'dark'
                  ? 'bg-[#050607]/70 border border-gray-800/50 hover:border-gray-700/70'
                  : 'bg-white/80 border border-gray-200 hover:border-gray-300'} p-6 transition-all duration-300 relative overflow-hidden group ${theme === 'dark' ? '' : 'light-card-shadow'}`}
                variants={itemVariants}
                whileHover={{ 
                  y: -5, 
                  boxShadow: theme === 'dark' 
                    ? "0 10px 30px -15px rgba(0, 255, 157, 0.15)" 
                    : "0 10px 30px -15px rgba(0, 204, 122, 0.25)" 
                }}
              >
                {/* Decorative gradient bar */}
                <div className={`absolute h-1 top-0 left-0 right-0 ${theme === 'dark'
                  ? 'bg-gradient-to-r from-primary/80 to-blue-500/80'
                  : 'bg-gradient-to-r from-primary-light/80 to-blue-500/80'} transform origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-500`} />

                {/* Card content */}
                <div className="flex flex-col gap-6">
                  {/* Profile image */}
                  <div className="relative w-full max-w-[300px] mx-auto">
                    <img
                      src={member.image}
                      alt={`${member.name}'s profile`}
                      className="h-full w-full object-cover aspect-square rounded-lg"
                      loading="lazy"
                    />
                    <div className={`absolute inset-0 bg-gradient-to-t ${theme === 'dark'
                      ? 'from-gray-900 to-transparent'
                      : 'from-gray-700 to-transparent'} opacity-60 rounded-lg`} />
                  </div>

                  {/* Member information */}
                  <div className="flex flex-col justify-between">
                    <div>
                      <h3 className={`text-2xl font-medium mb-2 text-transparent bg-clip-text ${theme === 'dark'
                        ? 'bg-gradient-to-r from-primary to-blue-400'
                        : 'bg-gradient-to-r from-primary-light to-blue-500'} text-center`}>
                        {member.name}
                      </h3>
                      <p className={`${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'} mb-2 font-medium text-center`}>{member.role}</p>
                      <p className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'} mb-4 text-center`}>{member.description}</p>
                    </div>
                    <div className="space-y-3">
                      <div className={`flex items-center justify-center ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                        <Mail className={`h-5 w-5 mr-3 ${theme === 'dark' ? 'text-primary' : 'text-primary-light'} flex-shrink-0`} />
                        <a href={`mailto:${member.email}`} className={`${theme === 'dark' ? 'hover:text-primary' : 'hover:text-primary-light'} transition-colors duration-200`}>
                          {member.email}
                        </a>
                      </div>
                      <div className={`flex items-center justify-center ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                        <Linkedin className={`h-5 w-5 mr-3 ${theme === 'dark' ? 'text-primary' : 'text-primary-light'} flex-shrink-0`} />
                        <a 
                          href={member.linkedin} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className={`${theme === 'dark' ? 'hover:text-primary' : 'hover:text-primary-light'} transition-colors duration-200`}
                        >
                          LinkedIn Profile
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div
            className="flex flex-col sm:flex-row justify-center gap-4 mt-16"
            variants={itemVariants}
          >
            <Button
              size="lg"
              className={`${theme === 'dark'
                ? 'bg-gradient-to-r from-primary to-blue-500 text-background hover:opacity-90 hover:shadow-[0_0_25px_rgba(0,255,157,0.3)]'
                : 'bg-gradient-to-r from-primary-light to-blue-500 text-white hover:opacity-90 hover:shadow-[0_0_25px_rgba(0,204,122,0.3)]'} 
                px-8 py-6 transition-all duration-300 transform hover:scale-105 relative overflow-hidden group`}
              onClick={handleNavigateHome}
            >
              <span className="relative z-10 flex items-center">
                Return to Home
                <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
              </span>
              <motion.span className={`absolute inset-0 ${theme === 'dark'
                ? 'bg-gradient-to-r from-blue-500 to-primary'
                : 'bg-gradient-to-r from-blue-500 to-primary-light'} opacity-0 group-hover:opacity-100 transition-opacity duration-500`} />
            </Button>
          </motion.div>
        </motion.div>
      </div>
      
      <Footer />
    </div>
  );
};

export default ContactUs;